/**
 * The drive-in spray bay in front of a respray shop: a marked, lit bay between two low block walls, its
 * mouth on the yard and its back on the shop's roller door. Pure geometry from the shop's POI, shared by
 * the world builder (the walls and their colliders), the client (the trigger, the gantry, garage mode) and
 * the online server (a purchase is only accepted with the car inside the bay).
 *
 * The POI heading points out of the shop, toward the road: a car drives in nose first (heading + π) and is
 * turned round by Pike's lads to drive out.
 */
import type { V2 } from '../core/math';

export interface SprayBay {
  /** POI id of the shop. */
  poi: string;
  /** Centre of the marked floor. */
  center: V2;
  /** Unit vector out of the bay (toward the road). */
  out: V2;
  /** Half length along `out` and half width across it (m): the marked floor, the trigger. */
  halfL: number;
  halfW: number;
  /** Heading of a car parked facing out of the bay. */
  heading: number;
}

/** How far the bay sits back from the POI toward the shop (m). */
export const BAY_INSET = 3.0;
export const BAY_HALF_L = 3.6;
export const BAY_HALF_W = 3.0;
/** Side walls stand this far out from the bay's centre line (m), and are this tall. */
export const BAY_WALL_OFFSET = 3.35;
export const BAY_WALL_HEIGHT = 1.1;
/** The walls run back this far behind the floor (to meet the shop front). */
export const BAY_WALL_BACK = 0.6;

export function bayFor(poi: { id: string; pos: V2; heading: number }): SprayBay {
  const out = { x: Math.sin(poi.heading), z: Math.cos(poi.heading) };
  return {
    poi: poi.id,
    center: { x: poi.pos.x - out.x * BAY_INSET, z: poi.pos.z - out.z * BAY_INSET },
    out,
    halfL: BAY_HALF_L,
    halfW: BAY_HALF_W,
    heading: poi.heading,
  };
}

/** Bay-local coordinates: `along` toward the mouth, `side` across (+ = right looking out). */
export function bayLocal(b: SprayBay, x: number, z: number, out: { along: number; side: number } = { along: 0, side: 0 }): { along: number; side: number } {
  const dx = x - b.center.x;
  const dz = z - b.center.z;
  out.along = dx * b.out.x + dz * b.out.z;
  out.side = dx * -b.out.z + dz * b.out.x;
  return out;
}

const _l = { along: 0, side: 0 };

/** Is (x, z) on the marked floor (with an optional margin, + = more lenient)? */
export function inBay(b: SprayBay, x: number, z: number, margin = 0): boolean {
  bayLocal(b, x, z, _l);
  return Math.abs(_l.along) <= b.halfL + margin && Math.abs(_l.side) <= b.halfW + margin;
}

/** The two low side walls (segments from the shop front to the mouth). */
export function bayWalls(b: SprayBay): [V2, V2][] {
  const o = b.out;
  const s = { x: -o.z, z: o.x };
  const back = -(b.halfL + BAY_WALL_BACK);
  const front = b.halfL - 0.2;
  const at = (along: number, side: number): V2 => ({ x: b.center.x + o.x * along + s.x * side, z: b.center.z + o.z * along + s.z * side });
  return [
    [at(back, BAY_WALL_OFFSET), at(front, BAY_WALL_OFFSET)],
    [at(back, -BAY_WALL_OFFSET), at(front, -BAY_WALL_OFFSET)],
  ];
}

/** Keep parked cars out of the bay and its mouth (the approach from the road). */
export function blocksBayApproach(b: SprayBay, p: V2): boolean {
  bayLocal(b, p.x, p.z, _l);
  return _l.along > -b.halfL - 1 && _l.along < b.halfL + 16 && Math.abs(_l.side) < b.halfW + 4;
}
